import type { AluminiumComposition, SpecificationListItem } from 'api';
import { Operand } from 'api';
import { translate } from 'i18n';
import type { ComponentProps } from 'react';

import { MaterialBreakdownItem } from '../../components/MaterialBreakdownItem/MaterialBreakdownItem';
import { formatPercentageFeature } from '../formatFeature/formatFeature';
import { formatInstructionWarning } from '../formatInstructionWarning/formatInstructionWarning';

type MaterialItemDisplayData = {
  rawValue: number;
  value: string;
  highlight?: boolean;
};

type CompositionItem = ComponentProps<typeof MaterialBreakdownItem>;

const shouldShowItem = (item: MaterialItemDisplayData | null | undefined, forceShow = false) => {
  if (!item) {
    return false;
  }
  if (forceShow) {
    return true;
  }
  return item.rawValue !== 0;
};

const buildMaterialItemDisplayData = (
  value: number | null | undefined,
  operand: Operand,
  instructions?: SpecificationListItem[]
): MaterialItemDisplayData | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const instruction = formatInstructionWarning(operand, instructions);
  return {
    highlight: instruction?.highlight ?? false,
    rawValue: value,
    value: formatPercentageFeature(value),
  };
};

export const buildAluminiumCompositionItems = (
  composition: AluminiumComposition,
  instructions?: SpecificationListItem[],
  options: { includeAllItems?: boolean; showAerosol?: boolean } = {}
) => {
  const { includeAllItems = false, showAerosol = false } = options;

  const totalAluminium =
    composition.ubc + composition.aluminium_aerosol + composition.food_can + composition.other_aluminium;
  const totalNonAluminium = 1 - totalAluminium;
  const otherAluminiumValue = !showAerosol
    ? composition.aluminium_aerosol + composition.other_aluminium
    : composition.other_aluminium;

  const componentDisplayData = {
    aluminium: {
      label: translate('backlog.breakdowns.materialLabels.aluminium', { namespace: 'features' }),
      value: formatPercentageFeature(totalAluminium),
    },
    aluminiumAerosol: buildMaterialItemDisplayData(
      composition.aluminium_aerosol,
      Operand.aluminium_aerosol,
      instructions
    ),
    foodCan: buildMaterialItemDisplayData(composition.food_can, Operand.food_can, instructions),
    nonAluminium: {
      label: translate('backlog.breakdowns.materialLabels.nonAluminium', { namespace: 'features' }),
      value: formatPercentageFeature(totalNonAluminium),
    },
    nonMetal: buildMaterialItemDisplayData(composition.non_metal, Operand.non_metal, instructions),
    otherAluminium: buildMaterialItemDisplayData(otherAluminiumValue, Operand.other_aluminium, instructions),
    otherMetal: buildMaterialItemDisplayData(composition.other_metal, Operand.other_metal, instructions),
    ubc: buildMaterialItemDisplayData(composition.ubc, Operand.ubc, instructions),
  };

  const targetAluminiumItems: CompositionItem[] = [];
  const nonTargetItems: CompositionItem[] = [];

  targetAluminiumItems.push({
    dataTestId: 'material-breakdown-aluminium',
    label: componentDisplayData.aluminium.label,
    value: componentDisplayData.aluminium.value,
    variant: 'bold',
  });

  if (shouldShowItem(componentDisplayData.ubc, includeAllItems)) {
    const ubc = componentDisplayData.ubc;
    if (ubc) {
      targetAluminiumItems.push({
        dataTestId: 'material-breakdown-ubc',
        highlight: ubc.highlight,
        label: translate('backlog.breakdowns.materialLabels.ubc', { namespace: 'features' }),
        value: ubc.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.otherAluminium, includeAllItems)) {
    const otherAluminium = componentDisplayData.otherAluminium;
    if (otherAluminium) {
      targetAluminiumItems.push({
        dataTestId: 'material-breakdown-other-aluminium',
        highlight: otherAluminium.highlight,
        label: translate('backlog.breakdowns.materialLabels.otherAluminium', { namespace: 'features' }),
        value: otherAluminium.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.foodCan, includeAllItems)) {
    const foodCan = componentDisplayData.foodCan;
    if (foodCan) {
      targetAluminiumItems.push({
        dataTestId: 'material-breakdown-aluminium-food-can',
        highlight: foodCan.highlight,
        label: translate('backlog.breakdowns.materialLabels.aluminiumFoodCan', { namespace: 'features' }),
        value: foodCan.value,
      });
    }
  }

  if (showAerosol && shouldShowItem(componentDisplayData.aluminiumAerosol, includeAllItems)) {
    const aluminiumAerosol = componentDisplayData.aluminiumAerosol;
    if (aluminiumAerosol) {
      targetAluminiumItems.push({
        dataTestId: 'material-breakdown-aluminium-aerosol',
        highlight: aluminiumAerosol.highlight,
        label: translate('backlog.breakdowns.materialLabels.aluminiumAerosol', { namespace: 'features' }),
        value: aluminiumAerosol.value,
      });
    }
  }

  nonTargetItems.push({
    dataTestId: 'material-breakdown-non-aluminium',
    label: componentDisplayData.nonAluminium.label,
    value: componentDisplayData.nonAluminium.value,
    variant: 'bold',
  });

  if (shouldShowItem(componentDisplayData.otherMetal, includeAllItems)) {
    const otherMetal = componentDisplayData.otherMetal;
    if (otherMetal) {
      nonTargetItems.push({
        dataTestId: 'material-breakdown-other-metal',
        highlight: otherMetal.highlight,
        label: translate('backlog.breakdowns.materialLabels.otherMetal', { namespace: 'features' }),
        value: otherMetal.value,
      });
    }
  }

  if (shouldShowItem(componentDisplayData.nonMetal, includeAllItems)) {
    const nonMetal = componentDisplayData.nonMetal;
    if (nonMetal) {
      nonTargetItems.push({
        dataTestId: 'material-breakdown-non-metal',
        highlight: nonMetal.highlight,
        label: translate('backlog.breakdowns.materialLabels.nonMetal', { namespace: 'features' }),
        value: nonMetal.value,
      });
    }
  }

  return {
    allValues: componentDisplayData,
    nonTargetSection: {
      items: nonTargetItems,
    },
    targetAluminiumSection: {
      items: targetAluminiumItems,
    },
  };
};
